<template>
  <app-page class="special-days">
    <h1 class="title mb-8">空き状況で探す</h1>

    <section
      v-if="m_cars.length === 0"
      class="mt-8"
    >
      <div>予約可能な車両が見つかりませんでした。</div>
    </section>
    <section
      v-else
      class="mt-8"
    >
      <div class="d-flex align-baseline mb-2">
        <h1 class="subtitle-1">利用する期間を選択して下さい</h1>
        <v-spacer/>
        <span>{{m_cars.length}} 件</span>
      </div>

      <v-row dense class="mb-4">
        <v-spacer/>
        <v-col cols="12" sm="auto" class="text-right">
          <span class="caption text--secondary mr-3">凡例</span>
          <div class="legend is-closed">定休日</div>
        </v-col>
        <v-col cols="12" sm="auto" class="text-right">
          <v-btn
            small
            color="primary"
            class="ml-3"
            @click="onMoveMonth(-1)"
            :disabled="first === mindate"
          >
            <v-icon>keyboard_arrow_left</v-icon>
            <span>1ヶ月前</span>
          </v-btn>
          <v-btn
            small
            color="primary"
            class="ml-2"
            @click="onMoveMonth(1)"
            :disabled="first === maxdate"
          >
            <span>1ヶ月後</span>
            <v-icon>keyboard_arrow_right</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <days-item
        v-for="car in m_cars"
        :key="car.id"

        :period="period"
        :dates="showDates"
        :car="car"
        :allows="allows[car.id] || []"

        @moveday="onMoveDay"
        @selected="onSelected"
      />
    </section>
  </app-page>
</template>

<style lang="scss">
@import '@/libs/vuetify/styles/variables.scss';

.special-days {
  .legend {
    display: inline-block;
    padding: .25rem .5rem;
    min-width: 4rem;
    text-align: center;
    @extend %typo-caption;
    font-weight: 700;

    &.is-closed {
      background-color: #66BB6A;
      color: white;
    }
  }
}
</style>

<script>
import { mapGetters, mapActions } from 'vuex'
import ShopBase from '@/app/special/mixins/ShopBase'
import DaysItem from './DaysItem'

import * as http from '@/libs/http'
import * as time from '@/libs/time'
import * as calendar from '@/app/special/libs/calendar'
import * as C from '@/app/special/consts'

export default {
  name: 'Days',
  mixins: [
    ShopBase('空き状況で探す')
  ],
  components: {
    DaysItem
  },
  data () {
    return {
      dates: [],
      allows: {}
    }
  },
  computed: {
    ...mapGetters('shop', [
      'profile',
      'm_cars',
      'm_calendar'
    ]),

    //
    // query.s の下限
    //
    mindate () {
      return this.m_calendar.period.begin
    },

    //
    // query.s の上限
    //
    maxdate () {
      const dt = time.make(this.m_calendar.period.end)
      dt.setDate(dt.getDate() - C.TIMELINE_DAYS + 1)
      return time.format('Y-m-d', dt)
    },

    //
    // 表示期間の開始日
    //
    first () {
      return this.$route.query.s || this.mindate
    },

    //
    // 表示期間の終了日
    //
    last () {
      const dt = time.make(this.first)
      dt.setDate(dt.getDate() + C.TIMELINE_DAYS)
      return time.format('Y-m-d', dt)
    },

    //
    // 予約可能期間
    //
    period () {
      const period = this.m_calendar.period
      const begin = time.make(period.begin)
      const end = time.make(period.end)

      begin.setDate(begin.getDate() - 1)
      end.setDate(end.getDate() + 1)

      return { begin, end }
    },

    //
    // 表示カレンダー
    //
    showDates () {
      const first = this.first
      const last = this.last

      return this.dates.filter(item => {
        return first <= item.date && item.date < last
      })
    }
  },
  created () {
    this.addFetcher(enter => this.fetch(enter))
  },
  methods: {
    ...mapActions('book', [
      'newBook'
    ]),

    async fetch (enter) {
      if (!enter) {
        return true
      }

      if (this.m_cars.length === 0) {
        return true
      }

      // 全車両の予約状況を取得する
      const carids = this.m_cars.map(item => item.id)
      const period = this.m_calendar.period
      const data = await http.request(async api => {
        const resp = await api.get(`shops/${this.shopID}/busy`, {
          params: {
            carid: carids,
            s: period.begin,
            e: period.end
          }
        })
        if (resp.status === http.NoContent) {
          return {}
        }
        if (resp.status === http.OK) {
          return resp.data
        }
        return new http.StatusError(resp)
      })

      // 各車の空き状況を作成する
      const cal = calendar.create(this.m_calendar, 'daysand')
      const allows = {}
      this.m_cars.forEach(car => {
        const busies = data[car.id] || []
        allows[car.id] = cal.allows(busies, this.profile.intervalHour)
      })
      this.allows = allows

      // 各日にちの営業時間を配列形式に変換する
      const d = this.m_calendar.dates
      const dates = Object.keys(d)
      dates.sort()
      this.dates = dates.map(date => {
        if (!d[date]) {
          return {
            date,
            begin: null,
            end: null,
            hi: false,
            holiday: false,
            closed: true
          }
        } else {
          return {
            date,
            begin: d[date].begin,
            end: d[date].end,
            hi: d[date].hi,
            saturday: (d[date].w === 6),
            holiday: d[date].holiday || (d[date].w === 0),
            closed: false
          }
        }
      })

      return true
    },

    //
    // 日付の移動イベント
    //
    onMoveMonth (delta) {
      const dt = time.make(this.first)
      dt.setMonth(dt.getMonth() + delta)
      let next = time.format('Y-m-d', dt)
      if (next < this.mindate) {
        next = this.mindate
      } else if (next > this.maxdate) {
        next = this.maxdate
      }

      if (this.first !== next) {
        this.$router.replace({ query: { s: next } })
      }
    },

    onMoveDay (delta) {
      const dt = time.make(this.first)
      dt.setDate(dt.getDate() + delta)
      const next = time.format('Y-m-d', dt)

      if (this.mindate <= next && next <= this.maxdate) {
        this.$router.replace({ query: { s: next } })
      }
    },

    //
    // 車両, 期間を選択された
    //
    async onSelected (e) {
      // begin を定義する
      const begin = e.period.begin

      // 7 日制約を設定
      const limit = time.clone(begin)
      limit.setDate(limit.getDate() + 7)

      // end を算出する
      const cal = calendar.create(this.m_calendar, 'daysand')
      let end = time.clone(begin)
      end.setDate(end.getDate() + 1)
      const close = cal.testClosed(end, false, true)
      if (!close) {
        // end は営業中
      } else if (close.end <= limit) {
        // close.end は 7 日以内
        end = close.end
      } else {
        // close.end が 7日以上なら、当日の閉店時間
        const d = time.format('Y-m-d', begin)
        end = time.join(d, this.m_calendar.dates[d].end)
      }

      if (e.allow.end < end) {
        end = e.allow.end
      }

      await this.newBook({
        allow: e.allow,
        period: { begin, end },
        carid: e.carid
      })

      // 予約期間入力へ
      this.$router.push({ name: 'book-period' })
    }
  }
}
</script>
